import { DEFAULT_REGION_BANK_HEADER_VALUE } from '@/constants'
import { formatProducts } from '@/pages/Cart/Cart.config'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { CartProductInterface } from '@/types'
import api from '@/utils/api/api'
import { fetchCart as fetchCartUtil } from '@/utils/api/fetch/fetchEntities'

import type {
  CartData,
  DeleteProductParams,
  UpdateProductQuantityParams,
} from './cartServiceTypes'

export class CartApiService {
  private getCurrentClientId(): string | undefined {
    const { currentClient, userOrganisation } = useUserInfoStore.getState()

    if (currentClient?.id) {
      return currentClient.id
    }

    if (userOrganisation?.id) {
      return userOrganisation.id
    }
  }

  async fetchCart(
    setProductsQuantity: (quantity: number) => void
  ): Promise<CartData> {
    const data = await fetchCartUtil({
      setProductsQuantity,
    })

    const content = data.cart?.cartItems?.content || []
    const formatted = formatProducts(content)
    const totalPrice = data.cart?.cartTotalPrice || 0

    return {
      products: formatted,
      totalPrice: totalPrice,
      productsQuantity: formatted.length,
    }
  }

  async deleteCart(): Promise<void> {
    const clientId = this.getCurrentClientId()
    const params = clientId ? { client: clientId } : {}
    await api.delete('/cart', { params })
  }

  async deleteProducts(products: CartProductInterface[]): Promise<CartData> {
    const clientId = this.getCurrentClientId()
    if (!clientId) {
      throw new Error('Клиент не выбран')
    }

    const itemIds = products.map((p) => p.id)

    const response = await api.post(
      '/cart/items/bulk-delete',
      {
        itemIds,
        client: clientId,
      },
      {
        headers: { 'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE },
      }
    )

    const content = response.data.cart?.cartItems?.content || []
    const formatted = formatProducts(content)
    const totalPrice = response.data.cart?.cartTotalPrice || 0

    return {
      products: formatted,
      totalPrice: totalPrice,
      productsQuantity: formatted.length,
    }
  }

  async deleteProductById(params: DeleteProductParams): Promise<CartData> {
    const clientId = this.getCurrentClientId()
    const queryParams = clientId ? { client: clientId } : {}

    const response = await api.delete(`/cart/items/${params.itemId}`, {
      headers: { 'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE },
      params: queryParams,
    })

    const content = response.data.cart?.cartItems?.content || []
    const formatted = formatProducts(content)
    const totalPrice = response.data.cart?.cartTotalPrice || 0

    return {
      products: formatted,
      totalPrice: totalPrice,
      productsQuantity: formatted.length,
    }
  }

  async updateItemQuantity(
    params: UpdateProductQuantityParams
  ): Promise<CartData> {
    const clientId = this.getCurrentClientId()
    const queryParams = clientId ? { client: clientId } : {}

    const response = await api.patch(
      `/cart/items/${params.itemId}`,
      { quantity: params.quantity },
      {
        headers: { 'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE },
        params: queryParams,
      }
    )

    const content = response.data.cart?.cartItems?.content || []
    const formatted = formatProducts(content)
    const totalPrice = response.data.cart?.cartTotalPrice || 0

    return {
      products: formatted,
      totalPrice: totalPrice,
      productsQuantity: formatted.length,
    }
  }
}

export const cartApiService = new CartApiService()
